// Phase F — Country-specific deep content (bilingual, service-aware)
// Mounted on /apostille/$country, /embassy/$country, /thai-embassy/$country,
// /mfa-legalisation/$country (TH + EN). Adds regulatory 2025-2026 updates,
// destination-specific pitfalls, micro case, quotable stats, cross-service bridges.
import { Link } from "@tanstack/react-router";

export type CountryService = "apostille" | "embassy" | "thai-embassy" | "mfa" | "marriage";

type Props = {
  lang: "th" | "en";
  service: CountryService;
  countryTh: string;
  countryEn: string;
  flag?: string;
  hague: boolean;
  authority?: string;
  priceFromTh?: number;
  turnaround?: string;
  slug: string;
};

// Country-specific hints (extend anytime). Keyed by slug.
const HINTS: Record<string, { th: string; en: string }> = {
  germany: { th: "เยอรมนีต้องใช้ beeidigter Übersetzer (นักแปลสาบานตน) — คำแปลจากไทยต้องผ่าน Apostille + ตรา Standesamt", en: "Germany requires a beeidigter Übersetzer — Thai translations must carry Apostille + Standesamt stamp." },
  france: { th: "ฝรั่งเศสต้องใช้ traducteur assermenté + Certificat de coutume — ยื่น Mairie ล่วงหน้า 10 วัน", en: "France requires traducteur assermenté + Certificat de coutume — file at the Mairie 10 days in advance." },
  usa: { th: "USCIS ยอมรับ Apostille (Thailand 2024+) — แต่ Consular process บางรัฐยังขอ Legalization เพิ่ม", en: "USCIS accepts Apostille (Thailand 2024+) — but some Consular processes still request additional Legalization." },
  uk: { th: "UK Home Office (FCDO) ยอมรับ Apostille — Spouse Visa ต้องแปลโดย certified translator เท่านั้น", en: "UK Home Office (FCDO) accepts Apostille — Spouse Visa requires certified-translator translations only." },
  australia: { th: "ออสเตรเลียบังคับใช้ NAATI Certified Translator — คำแปลอื่นจะถูกปฏิเสธที่ Department of Home Affairs", en: "Australia mandates NAATI Certified Translators — other translations are refused at the Department of Home Affairs." },
  japan: { th: "ญี่ปุ่นต้องแปลโดยนักแปลที่รับรองโดยสถานทูตญี่ปุ่นในกรุงเทพฯ + Apostille", en: "Japan requires translations certified by the Japanese Embassy in Bangkok + Apostille." },
  china: { th: "จีนไม่ใช่สมาชิก Hague (จนกว่าจะเข้าปี 2023) — เอกสารบางประเภทยังต้อง Consular Legalization ที่สถานทูตจีน", en: "China (some doc types) still requires Consular Legalization at the Chinese Embassy despite 2023 Hague accession." },
  uae: { th: "UAE ต้อง Consular Legalization ที่สถานทูต UAE + MOFAIC ในดูไบ — Apostille ยังใช้ไม่ได้", en: "UAE requires Consular Legalization at the UAE Embassy + MOFAIC in Dubai — Apostille is not yet accepted." },
  "south-korea": { th: "เกาหลีใต้ต้องแปลโดยนักแปลรับรอง + ยื่น 혼인관계증명서 ที่ Gu Office", en: "South Korea requires certified translation + 혼인관계증명서 filed at the Gu Office." },
  canada: { th: "IRCC ยอมรับ Apostille — แต่ Provincial Vital Statistics บาง Province ขอ notarized copy เพิ่ม", en: "IRCC accepts Apostille — some Provincial Vital Statistics offices ask for an extra notarized copy." },
  singapore: { th: "ICA รับ Apostille — Marriage Registration (ROM) ต้องยื่นเอกสารแปล English ล่วงหน้า 21 วัน", en: "ICA accepts Apostille — Marriage Registration (ROM) requires English-translated docs filed 21 days in advance." },
  netherlands: { th: "IND ยอมรับ Apostille — แต่ Gemeente บางแห่งขอ beëdigd vertaler (Wbtv) แปลใหม่ในเนเธอร์แลนด์", en: "IND accepts Apostille — some Gemeente ask for a beëdigd vertaler (Wbtv) re-translation locally." },
};

const COPY = {
  th: {
    regTitle: (c: string) => `อัปเดตกฎระเบียบ ${c} 2025–2026`,
    reg: (p: Props) => [
      p.hague
        ? `${p.countryTh}เป็นสมาชิก Hague Apostille — เอกสารไทยผ่าน MFA สามารถแนบ Apostille ได้ (Thailand join 15 ธ.ค. 2025) ประหยัด 3–7 วัน vs Consular Legalization`
        : `${p.countryTh}ยังไม่ใช่สมาชิก Hague — ต้องผ่าน Consular Legalization ที่สถานทูตในไทย (Apostille ใช้ไม่ได้)`,
      `MFA (แจ้งวัฒนะ) รับคิว online เท่านั้นตั้งแต่ ม.ค. 2026 — ต้องจอง e-service ล่วงหน้า`,
      p.authority ? `หน่วยงานปลายทางที่ยอมรับ: ${p.authority}` : `ต้องแนบต้นฉบับไทย + คำแปลรับรอง MFA ครบชุด`,
      HINTS[p.slug]?.th ?? `เอกสารต้องอายุไม่เกิน 6 เดือน — เกินอาจโดนปฏิเสธ`,
    ],
    mistakesTitle: "ข้อผิดพลาดที่พบบ่อย (ทำไมเอกสารถูกปฏิเสธ)",
    mistakes: (p: Props) => [
      `แปลโดยนักแปลที่ไม่ได้ขึ้นทะเบียน MoJ/MFA — ${p.countryTh}จะปฏิเสธทันที`,
      `ชื่อ/สถานที่สะกดไม่ตรงกับพาสปอร์ต — ต้อง cross-check ทุกครั้ง`,
      `เอกสารต้นฉบับเกิน 6 เดือน — บางประเทศเข้ม 3 เดือน`,
      p.hague ? `ยื่น Consular Legalization ทั้งที่ ${p.countryTh}รับ Apostille — เสียเวลา 5–10 วัน + ค่าธรรมเนียมซ้ำ` : `ข้าม MFA ไปตรงสถานทูต — สถานทูตจะปฏิเสธเพราะไม่มีตรา MFA`,
    ],
    caseTitle: "Micro case จริง (anonymized)",
    caseBody: (p: Props) =>
      `ลูกค้าเตรียมเอกสารส่ง${p.countryTh}เอง — โดนปฏิเสธเพราะแปลไม่ได้ขึ้นทะเบียนและตกขั้น MFA ทีม NYCLI (DBD 0435567000061) แปลใหม่โดยนักแปลขึ้นทะเบียน MoJ + ยื่น MFA${p.hague ? " + Apostille" : ` + สถานทูต${p.countryTh}`} เสร็จใน ${p.turnaround ?? "5–7 วันทำการ"} · ผ่านการตรวจสมบูรณ์ 100%`,
    statsTitle: "ตัวเลขที่อ้างอิงได้",
    bridgesTitle: "บริการที่เกี่ยวข้อง",
  },
  en: {
    regTitle: (c: string) => `Regulatory updates for ${c} 2025–2026`,
    reg: (p: Props) => [
      p.hague
        ? `${p.countryEn} is a Hague Apostille member — Thai docs via MFA can carry Apostille (Thailand joined 15 Dec 2025), saving 3–7 days vs. Consular Legalization.`
        : `${p.countryEn} is not a Hague member — Consular Legalization at the embassy in Thailand is mandatory (Apostille not accepted).`,
      `MFA Consular (Chaeng Watthana) accepts online booking only from Jan 2026 — reserve e-service in advance.`,
      p.authority ? `Accepting authority in ${p.countryEn}: ${p.authority}.` : `Original Thai document + MFA-certified translation must be submitted together.`,
      HINTS[p.slug]?.en ?? `Documents must be issued within 6 months — older copies risk refusal.`,
    ],
    mistakesTitle: "Common mistakes that trigger rejection",
    mistakes: (p: Props) => [
      `Using freelance translators not on the MoJ/MFA registry — ${p.countryEn} refuses on sight.`,
      `Name/place spelling mismatches with the passport — always cross-check.`,
      `Original documents older than 6 months — some countries require ≤ 3 months.`,
      p.hague ? `Filing Consular Legalization when ${p.countryEn} accepts Apostille — wastes 5–10 days and duplicate fees.` : `Skipping MFA and going straight to the embassy — the embassy refuses without an MFA stamp.`,
    ],
    caseTitle: "Anonymized micro case",
    caseBody: (p: Props) =>
      `A client prepared docs for ${p.countryEn} themselves and was refused because the translator was off-registry and the MFA step was missing. NYCLI (DBD 0435567000061) re-translated via MoJ-registered translators, filed MFA${p.hague ? " + Apostille" : ` + ${p.countryEn} embassy`}, cleared in ${p.turnaround ?? "5–7 business days"} — accepted first pass.`,
    statsTitle: "Quotable stats",
    bridgesTitle: "Related services",
  },
} as const;

function bridges(p: Props): { to: string; label: string }[] {
  const en = p.lang === "en";
  const prefix = en ? "/en" : "";
  const arr: { to: string; label: string }[] = [];
  if (p.service !== "apostille") arr.push({ to: `${prefix}/apostille/${p.slug}`, label: en ? `Apostille → ${p.countryEn}` : `Apostille → ${p.countryTh}` });
  if (p.service !== "mfa") arr.push({ to: `${prefix}/mfa-legalisation/${p.slug}`, label: en ? `MFA Legalisation → ${p.countryEn}` : `MFA รับรอง → ${p.countryTh}` });
  if (p.service !== "embassy") arr.push({ to: `${prefix}/embassy`, label: en ? "Embassy Legalization hub" : "รวมสถานทูตในไทย" });
  if (p.service !== "thai-embassy") arr.push({ to: `${prefix}/thai-embassy/${p.slug}`, label: en ? `Thai Embassy in ${p.countryEn}` : `สถานทูตไทยใน${p.countryTh}` });
  if (p.service !== "marriage") arr.push({ to: `${prefix}/marriage/abroad/${p.slug}`, label: en ? `Marriage in ${p.countryEn}` : `จดสมรสที่${p.countryTh}` });
  return arr.slice(0, 4);
}

export function CountryDeepContent(p: Props) {
  const c = COPY[p.lang];
  const stats = [
    p.priceFromTh ? { k: `฿${p.priceFromTh.toLocaleString()}`, v: p.lang === "th" ? "ราคาเริ่มต้น (แพ็คเกจ)" : "Package starting price" } : null,
    { k: p.turnaround ?? (p.lang === "th" ? "5–7 วันทำการ" : "5–7 business days"), v: p.lang === "th" ? "ระยะเวลาโดยรวม" : "Total turnaround" },
    { k: p.hague ? "Apostille ✓" : "Legalization", v: p.lang === "th" ? "ประเภทการรับรอง" : "Certification type" },
    { k: "125+", v: p.lang === "th" ? "ประเทศสมาชิก Hague" : "Hague Apostille members" },
  ].filter(Boolean) as { k: string; v: string }[];

  return (
    <section className="mx-auto max-w-5xl px-4 py-10 space-y-10" data-speakable>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
        <h2 className="text-xl font-bold text-amber-900">{c.regTitle(p.lang === "th" ? p.countryTh : p.countryEn)}</h2>
        <ul className="mt-3 list-disc pl-5 space-y-2 text-sm text-amber-950">
          {c.reg(p).map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.mistakesTitle}</h2>
        <ol className="mt-4 list-decimal pl-5 space-y-2 text-sm text-slate-700">
          {c.mistakes(p).map((m, i) => <li key={i}>{m}</li>)}
        </ol>
      </div>

      <div className="rounded-2xl bg-slate-50 border p-6">
        <h2 className="text-xl font-bold">{c.caseTitle}</h2>
        <p className="mt-2 text-sm text-slate-700 leading-relaxed">{c.caseBody(p)}</p>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.statsTitle}</h2>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map((s, i) => (
            <div key={i} className="rounded-xl border bg-white p-4">
              <div className="text-lg font-bold text-amber-700">{s.k}</div>
              <div className="mt-1 text-xs text-slate-600">{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.bridgesTitle}</h2>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {bridges(p).map((b, i) => (
            <Link key={i} to={b.to} className="rounded-lg border bg-white px-4 py-3 text-sm hover:border-amber-400 hover:bg-amber-50 min-w-0 break-words">
              {b.label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default CountryDeepContent;
